/**
 * Who can see which module, for drawing the navigation.
 *
 * This is a copy. The original is backend/src/config/modulePermissions.ts,
 * which is what actually refuses a request — this file only decides what to
 * put on screen, so menus render immediately instead of waiting on a round
 * trip. A test in the backend (src/__tests__/modulePermissions.test.ts) fails
 * if the two disagree.
 *
 * If you change one, change the other.
 */

export const ACCOUNTING_SECTIONS = [
  'AR',
  'AP',
  'LEDGER',
  'BANKING',
  'REPORTS',
  'ADVANCED',
  'SETTINGS',
] as const;

export type AccountingSection = (typeof ACCOUNTING_SECTIONS)[number];

export interface RoleAccess {
  hr: boolean;
  accounting: AccountingSection[];
  label: string;
}

export const ROLE_ACCESS: Record<string, RoleAccess> = {
  SUPER_ADMIN: { hr: true, accounting: ['AR', 'AP', 'LEDGER', 'BANKING', 'REPORTS', 'ADVANCED', 'SETTINGS'], label: 'Super Admin' },
  ADMIN: { hr: false, accounting: ['AR', 'AP'], label: 'Admin' },
  SALES: { hr: false, accounting: ['AR'], label: 'Sales' },
};

const NO_ACCESS: RoleAccess = { hr: false, accounting: [], label: 'Other user' };

export function accessFor(role?: string | null): RoleAccess {
  if (!role) return NO_ACCESS;
  return ROLE_ACCESS[role] ?? NO_ACCESS;
}

export function canSeeHr(role?: string | null): boolean {
  return accessFor(role).hr;
}

export function canSeeAccounting(role?: string | null): boolean {
  return accessFor(role).accounting.length > 0;
}

export function canSeeAccountingSection(
  role: string | null | undefined,
  section: AccountingSection,
): boolean {
  return accessFor(role).accounting.includes(section);
}

/**
 * Which section each item in the Accounting sub-navigation belongs to, keyed by
 * the menu key used in AccountingOverview. A key missing from here is treated
 * as Super Admin only, matching how the backend handles an unclassified route.
 */
export const ACCOUNTING_NAV_SECTION: Record<string, AccountingSection> = {
  overview: 'REPORTS',

  receivables: 'AR',
  quotations: 'AR',
  invoices: 'AR',
  'credit-notes': 'AR',
  'invoice-templates': 'AR',
  'ar-aging': 'AR',
  'customer-statement': 'AR',

  payables: 'AP',
  vendors: 'AP',
  'vendor-bills': 'AP',
  'recurring-bills': 'AP',
  'ap-aging': 'AP',
  'vendor-statement': 'AP',

  ledger: 'LEDGER',
  accounts: 'LEDGER',
  journals: 'LEDGER',
  journal: 'LEDGER',
  'general-ledger': 'LEDGER',

  banking: 'BANKING',
  payments: 'BANKING',
  'bank-reconciliation': 'BANKING',

  reports: 'REPORTS',

  'advanced-features': 'ADVANCED',
  'deferred-revenue': 'ADVANCED',
  'deferred-expense': 'ADVANCED',

  'accounting-settings': 'SETTINGS',
};

/**
 * The Accounting entries in the left sidebar. These use route-style keys rather
 * than the tab keys above, so they need their own map — miss one and the tab
 * disappears from the top of the page while the sidebar still offers a way in.
 */
export const ACCOUNTING_SIDEBAR_SECTION: Record<string, AccountingSection> = {
  'accounting': 'REPORTS',                    // Overview
  'accounting/chart-of-accounts': 'LEDGER',
  'accounting/journal-entries': 'LEDGER',
  'accounting/invoices': 'AR',
  'accounting/vendor-bills': 'AP',
  'accounting/reports': 'REPORTS',
};

/** Whether a role may open a given entry in the Accounting sidebar. */
export function canSeeAccountingSidebarItem(role: string | null | undefined, key: string): boolean {
  const section = ACCOUNTING_SIDEBAR_SECTION[key];
  if (!section) return accessFor(role).accounting.length === ACCOUNTING_SECTIONS.length;
  return canSeeAccountingSection(role, section);
}

/** Whether a role may open a given tab or dropdown item in Accounting. */
export function canSeeAccountingTab(role: string | null | undefined, key: string): boolean {
  const section = ACCOUNTING_NAV_SECTION[key];
  if (!section) return accessFor(role).accounting.length === ACCOUNTING_SECTIONS.length;
  return canSeeAccountingSection(role, section);
}

/**
 * Drop the tabs a role may not open, and any dropdown items beneath the ones it
 * keeps. Applied to the Accounting navigation so Admin sees Receivables and
 * Payables, and Sales sees Receivables.
 */
export function filterAccountingTabs<T extends { key: string; children?: T[] }>(
  role: string | null | undefined,
  items: T[],
): T[] {
  return items
    .filter(item => canSeeAccountingTab(role, item.key))
    .map(item =>
      item.children
        ? { ...item, children: item.children.filter(child => canSeeAccountingTab(role, child.key)) }
        : item,
    );
}

/**
 * Where to send a role when it opens Accounting. Admin and Sales cannot see the
 * Overview, so they land on the first screen they are allowed to use.
 */
export function landingTabFor(role?: string | null): string {
  const access = accessFor(role);
  if (access.accounting.includes('REPORTS')) return 'overview';
  if (access.accounting.includes('AR')) return 'invoices';
  if (access.accounting.includes('AP')) return 'vendor-bills';
  return 'overview';
}
